import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SidebarMode } from "../data/ManageDesks.model";
import { AppState } from '@app/store/state/app.state';


export interface PendingDeskEdit {
  x?: number;
  y?: number;
  rotation?: number;
}

export interface PendingAreaEdit {
  areaX?: number;
  areaY?: number;
  areaWidth?: number;
  areaHeight?: number;
  label?: string;
  capacity?: number;
  isNew?: boolean;
}

export interface MapViewport {
  positionX: number;
  positionY: number;
  scale: number;
  width: number;
  height: number;
}

export interface ManageDesksState {
  sidebarMode: SidebarMode
  selectedDeskIds: number[];
  deskEdits: {[deskId: number]: PendingDeskEdit};
  draggingDeskId?: number;
  areaEdits: {[areaOfficeId: number]: PendingAreaEdit};
  draggingAreaId?: number;
  selectedAreaId?: number;
  // New areas live under negative ids until they are created on save
  nextTempAreaId: number;
  mapViewport?: MapViewport;
}

export const initialManageDesksState: ManageDesksState = {
  sidebarMode: SidebarMode.ChooseOffice,
  selectedDeskIds: [],
  deskEdits: {},
  draggingDeskId: undefined,
  areaEdits: {},
  draggingAreaId: undefined,
  selectedAreaId: undefined,
  nextTempAreaId: -1,
  mapViewport: undefined,
};

const declineRequest: any = createAsyncThunk(
  'manageDesks/declineRequest',
  async (test: any, thunkAPI) => {
  }
);


const manageDesksSlice = createSlice({
  name: 'manageDesks',
  initialState: initialManageDesksState,
  reducers: {
    resetManageDesks: (state) => (initialManageDesksState),
    setSidebarMode: (state, action) => ({
      ...state, sidebarMode: action.payload, selectedDeskIds: [],
      deskEdits: {}, draggingDeskId: undefined,
      areaEdits: {}, draggingAreaId: undefined, selectedAreaId: undefined,
    }),
    setSelectedDeskIds: (state, action) => ({...state, selectedDeskIds: action.payload}),
    addSelectedDeskId: (state, action) => ({...state, selectedDeskIds: [...state.selectedDeskIds, action.payload]}),
    removeSelectedDeskId: (state, action) => ({...state, selectedDeskIds: [...state.selectedDeskIds].filter(id => id !== action.payload)}),
    clearSelectedDeskIds: (state) => ({...state, selectedDeskIds: []}),

    // Desk editing (beta): deskEdits holds unsaved position/rotation keyed by desk id.
    // Callers pass the desk's current merged value so edits accumulate from the right base.
    startDeskMove: (state, action: PayloadAction<{id: number, x: number, y: number}>) => {
      const existing = state.deskEdits[action.payload.id];
      return {
        ...state,
        draggingDeskId: action.payload.id,
        deskEdits: {
          ...state.deskEdits,
          [action.payload.id]: {
            ...existing,
            x: existing?.x ?? action.payload.x,
            y: existing?.y ?? action.payload.y,
          },
        },
      };
    },
    // dx/dy are pre-divided by the current map scale by the caller
    moveDeskBy: (state, action: PayloadAction<{dx: number, dy: number}>) => {
      if (state.draggingDeskId === undefined) return state;
      const current = state.deskEdits[state.draggingDeskId];
      if (!current || current.x === undefined || current.y === undefined) return state;
      return {
        ...state,
        deskEdits: {
          ...state.deskEdits,
          [state.draggingDeskId]: {...current, x: current.x + action.payload.dx, y: current.y + action.payload.dy},
        },
      };
    },
    endDeskMove: (state) => ({...state, draggingDeskId: undefined}),
    revertDeskMove: (state, action: PayloadAction<number>) => {
      const edits = {...state.deskEdits};
      delete edits[action.payload];
      return {...state, deskEdits: edits};
    },
    rotateDeskBy: (state, action: PayloadAction<{id: number, degrees: number, baseRotation: number}>) => {
      const existing = state.deskEdits[action.payload.id];
      return {
        ...state,
        deskEdits: {
          ...state.deskEdits,
          [action.payload.id]: {
            ...existing,
            rotation: (existing?.rotation ?? action.payload.baseRotation) + action.payload.degrees,
          },
        },
      };
    },
    // Area editing (beta): areas are child office entities carrying their own rectangle
    // A new area is held locally until save creates it in the backend
    addPendingArea: (state, action: PayloadAction<{label: string, x: number, y: number, width: number, height: number}>) => {
      const tempId = state.nextTempAreaId;
      return {
        ...state,
        nextTempAreaId: tempId - 1,
        selectedAreaId: tempId,
        areaEdits: {
          ...state.areaEdits,
          [tempId]: {
            areaX: action.payload.x,
            areaY: action.payload.y,
            areaWidth: action.payload.width,
            areaHeight: action.payload.height,
            label: action.payload.label,
            isNew: true,
          },
        },
      };
    },
    renameArea: (state, action: PayloadAction<{id: number, label: string}>) => ({
      ...state,
      areaEdits: {
        ...state.areaEdits,
        [action.payload.id]: {...state.areaEdits[action.payload.id], label: action.payload.label},
      },
    }),
    setAreaCapacity: (state, action: PayloadAction<{id: number, capacity: number}>) => ({
      ...state,
      areaEdits: {
        ...state.areaEdits,
        [action.payload.id]: {...state.areaEdits[action.payload.id], capacity: action.payload.capacity},
      },
    }),
    selectArea: (state, action: PayloadAction<number | undefined>) => ({...state, selectedAreaId: action.payload}),
    setMapViewport: (state, action: PayloadAction<MapViewport>) => ({...state, mapViewport: action.payload}),
    startAreaMove: (state, action: PayloadAction<{id: number, x: number, y: number}>) => {
      const existing = state.areaEdits[action.payload.id];
      return {
        ...state,
        draggingAreaId: action.payload.id,
        selectedAreaId: action.payload.id,
        areaEdits: {
          ...state.areaEdits,
          [action.payload.id]: {
            ...existing,
            areaX: existing?.areaX ?? action.payload.x,
            areaY: existing?.areaY ?? action.payload.y,
          },
        },
      };
    },
    moveAreaBy: (state, action: PayloadAction<{dx: number, dy: number}>) => {
      if (state.draggingAreaId === undefined) return state;
      const current = state.areaEdits[state.draggingAreaId];
      if (!current || current.areaX === undefined || current.areaY === undefined) return state;
      return {
        ...state,
        areaEdits: {
          ...state.areaEdits,
          [state.draggingAreaId]: {...current, areaX: current.areaX + action.payload.dx, areaY: current.areaY + action.payload.dy},
        },
      };
    },
    endAreaMove: (state) => ({...state, draggingAreaId: undefined}),
    revertAreaMove: (state, action: PayloadAction<number>) => {
      const edits = {...state.areaEdits};
      delete edits[action.payload];
      return {...state, areaEdits: edits};
    },
    resizeArea: (state, action: PayloadAction<{id: number, width: number, height: number}>) => {
      const existing = state.areaEdits[action.payload.id];
      return {
        ...state,
        areaEdits: {
          ...state.areaEdits,
          [action.payload.id]: {...existing, areaWidth: action.payload.width, areaHeight: action.payload.height},
        },
      };
    },
    clearDeskEdits: (state) => ({
      ...state, deskEdits: {}, draggingDeskId: undefined,
      areaEdits: {}, draggingAreaId: undefined, selectedAreaId: undefined,
    }),
  },
  extraReducers: (builder) => {},
});



export default manageDesksSlice.reducer;
export const {
  resetManageDesks, setSidebarMode, setSelectedDeskIds, clearSelectedDeskIds, addSelectedDeskId, removeSelectedDeskId,
  startDeskMove, moveDeskBy, endDeskMove, revertDeskMove, rotateDeskBy, clearDeskEdits,
  startAreaMove, moveAreaBy, endAreaMove, revertAreaMove, resizeArea,
  addPendingArea, renameArea, setAreaCapacity, selectArea, setMapViewport,
} = manageDesksSlice.actions;


// Selectors
export const selectSidebarMode = (state: AppState) => state.manageDesks.sidebarMode;
export const selectSelectedDeskIds = (state: AppState) => state.manageDesks.selectedDeskIds;
export const selectDeskEdits = (state: AppState) => state.manageDesks.deskEdits;
export const selectDraggingDeskId = (state: AppState) => state.manageDesks.draggingDeskId;
export const selectAreaEdits = (state: AppState) => state.manageDesks.areaEdits;
export const selectDraggingAreaId = (state: AppState) => state.manageDesks.draggingAreaId;
export const selectSelectedAreaId = (state: AppState) => state.manageDesks.selectedAreaId;
export const selectMapViewport = (state: AppState) => state.manageDesks.mapViewport;
// Counts desks and areas together — they share one save button
export const selectPendingDeskEditCount = (state: AppState) =>
  Object.keys(state.manageDesks.deskEdits).length + Object.keys(state.manageDesks.areaEdits).length;
const isDeskSelectionEnabled = (state: AppState) => state.manageDesks.sidebarMode === SidebarMode.ManageDesks;
